/* eslint-disable jsx-a11y/label-has-associated-control, jsx-a11y/label-has-for */
import { FunctionComponent, useCallback, useEffect, useRef } from 'react';
import classnames from 'classnames';

import { Button, ButtonText } from '@andes/button';

import type { HubStepsInfo, Step } from '../../../../services/types';
import { type ContactWordings, type HubWordings } from '../../../wordings';
import StepList from './step/list';
import { handleExecuteNative } from '../../../utils/executeNative';
import { type PageContextType, usePage } from '../../../contexts/PageContext';
import { StepsProvider } from './step/StepsContext';
import { MelidataTrack } from '../../../../services/Track';
import CenterCard from '../../../components/center-card';
import type { HubB2BWordings } from '../../../wordings/hub/b2b';
import { B2BComponent } from './B2B';

export type ViewProps = {
  wordings: {
    hubWordings: HubWordings,
    contactWordings: ContactWordings,
    stepsPendingWordings: HubWordings,
    hubB2bWordings?: HubB2BWordings,
  }
  hubInfo: HubStepsInfo
  urls: {
    declaredEmail: string
    feedbackStep: string
    b2b?: string
  },
  editDeclaredEmailTrack: MelidataTrack
  contactCXTrack: MelidataTrack
  animatedCongratsStep: Step | undefined
  enabledContact: boolean
  configs: {
    parityFlow?: boolean,
    hubB2b?: boolean,
  }
}

const HubView: FunctionComponent<ViewProps> = (props: ViewProps) => {
  const { configs = {}, wordings, hubInfo, urls, editDeclaredEmailTrack, contactCXTrack, animatedCongratsStep, enabledContact } = props;
  const { hubWordings, contactWordings, stepsPendingWordings, hubB2bWordings } = wordings;
  const { isMobile, isWebview } = usePage() as PageContextType;
  const { steps } = hubInfo;

  const ref = useRef<HTMLLabelElement>(null);

  const hubClassnames = classnames(
    'hub-card',
    { 'hub-card--mobile': isMobile },
    { 'hub-card--webview': isWebview },
  );

  const titleClassnames = classnames(
    'hub-card__title',
    { 'hub-card__title--webview': isWebview },
  );

  const handleCompleteHub = useCallback(() => {
    handleExecuteNative(
      isWebview,
      hubInfo.links.last.uri!,
      'push',
      {},
    );
  }, [isWebview, hubInfo]);

  const handleTrack = (track: MelidataTrack) => {
    const { melidata } = window;

    if (melidata) {
      melidata('send', 'event', track);
    }
  };

  useEffect(() => {
    if (enabledContact) {
      const container = ref.current!;
      const anchor = container?.querySelector('#contact-cx') as HTMLAnchorElement;

      if (anchor) anchor.onclick = () => handleTrack(contactCXTrack);
    }

    if (animatedCongratsStep && (hubInfo.status === 'finished' || hubInfo.status === 'under_age_finished')) {
      setTimeout(() => {
        handleCompleteHub();
      }, 2000);
    }
  }, [contactCXTrack, enabledContact, handleCompleteHub, hubInfo, animatedCongratsStep]);
  return (
    <>
      <CenterCard padding={0} className={hubClassnames}>
        <div className="hub-card__container">
          <h1 className={titleClassnames}>{hubWordings.title}</h1>
          <p className="hub-card__caption">{hubWordings.description}</p>

          <StepsProvider
            steps={steps}
            registrationID={hubInfo.registration_id}
            urlToDeclaredEmail={urls.declaredEmail}
            editDeclaredEmailTrack={editDeclaredEmailTrack}
            parityFlow={configs.parityFlow}
          >
            <StepList
              wordings={hubWordings}
              stepsPendingWordings={stepsPendingWordings}
              animatedCongratsStep={animatedCongratsStep}
              feedbackStepUrl={urls.feedbackStep}
            />
          </StepsProvider>
        </div>
        {!animatedCongratsStep && (hubInfo.status === 'finished' || hubInfo.status === 'under_age_finished')
        && (
          <>
            <Button
              id="hub-card-continue-button"
              fullWidth={isMobile}
              className="hub-card__action hub-card__action--continue"
              onClick={handleCompleteHub}
            >
              <ButtonText>{hubWordings.actions.continue}</ButtonText>
            </Button>
          </>
        )}
        {
        enabledContact && (
          <div className="hub-card__contact" data-cy="text-contact">
            <span
              ref={ref}
              dangerouslySetInnerHTML={{
                __html: contactWordings,
              }}
            />
          </div>
        )
      }
      </CenterCard>
      {
        configs.hubB2b
        && hubB2bWordings
        && <B2BComponent urls={urls} wordings={hubB2bWordings} />
      }
    </>
  );
};

export default HubView;
